import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import { userAPI } from '../services/api';

import { useLanguage } from '../context/LanguageContext';
import { 
  Shield, Key, Smartphone, Monitor, Globe,
  ChevronRight, AlertTriangle, Lock, Unlock, RefreshCw
} from 'lucide-react';
import {
  ChangePasswordModal,
  TwoFactorModal,
  ActiveSessionsModal,
  IPWhitelistModal
} from '../components/profile/SecurityModals';

const SecurityPage = () => {
  const { t } = useLanguage();
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState('none');
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Modals
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showIPModal, setShowIPModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSecurityStatus();
  }, []);

  const loadSecurityStatus = async () => {
    try {
      const res = await userAPI.get2FAStatus();
      setIs2FAEnabled(res.data.enabled);
      setTwoFactorMethod(res.data.method || 'none');
    } catch (err) {
      console.error('Failed to load security status');
    } finally {
      setLoading(false);
    }
  };

  const securityItems = [
    {
      icon: Key,
      title: t('changePassword'),
      description: t('newPassword'),
      status: null,
      action: () => setShowPasswordModal(true),
    },
    {
      icon: Smartphone,
      title: t('twoFactorAuth'),
      description: is2FAEnabled 
        ? (twoFactorMethod === 'authenticator' ? t('twoFAAuthenticator') : twoFactorMethod === 'email' ? t('twoFAEmail') : twoFactorMethod === 'both' ? t('twoFABoth') : t('enable2FA'))
        : t('disable2FA'),
      status: is2FAEnabled ? 'enabled' : 'disabled',
      action: () => setShow2FAModal(true),
    },
    {
      icon: Monitor,
      title: t('activeSessions'),
      description: t('settings'),
      status: null,
      action: () => setShowSessionsModal(true),
    },
    {
      icon: Globe,
      title: t('ipWhitelist'),
      description: t('ipAddress'),
      status: null,
      action: () => setShowIPModal(true),
    },
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: COLORS.textSecondary 
      }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? '16px' : '32px', 
      maxWidth: '800px', 
      margin: '0 auto',
      paddingBottom: isMobile ? '100px' : '32px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
        <h1 style={{ 
          color: COLORS.text, 
          fontSize: isMobile ? '22px' : '28px', 
          fontWeight: '700',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Shield size={isMobile ? 22 : 28} color={COLORS.primary} />
          {t('security')}
        </h1>
        {!isMobile && (
          <p style={{ color: COLORS.textSecondary, fontSize: '14px', marginTop: '8px' }}>
            {t('settings')}
          </p>
        )}
      </div>

      {/* Security Status Card - Mobile optimized */}
      <div style={{
        backgroundColor: is2FAEnabled ? '#0ecb8115' : '#f6465d15',
        border: `1px solid ${is2FAEnabled ? '#0ecb8140' : '#f6465d40'}`,
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '20px' : '32px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '14px' : '20px',
          flexDirection: isMobile ? 'row' : 'row',
        }}>
          <div style={{
            width: isMobile ? '44px' : '56px',
            height: isMobile ? '44px' : '56px',
            minWidth: isMobile ? '44px' : '56px',
            borderRadius: '50%',
            backgroundColor: is2FAEnabled ? '#0ecb81' : '#f6465d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {is2FAEnabled ? <Lock size={isMobile ? 22 : 28} color="#000" /> : <Unlock size={isMobile ? 22 : 28} color="#fff" />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              color: COLORS.text, 
              fontSize: isMobile ? '15px' : '18px', 
              fontWeight: '600',
              marginBottom: '2px'
            }}>
              {is2FAEnabled ? t('accountProtected') : t('accountNotProtected')}
            </div>
            <div style={{ 
              color: COLORS.textSecondary, 
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: '1.4'
            }}>
              {is2FAEnabled 
                ? t('twoFAEnabled')
                : t('enable2FAForProtection')}
            </div>
          </div>
        </div>
        
        {!is2FAEnabled && (
          <button
            onClick={() => setShow2FAModal(true)}
            style={{
              width: '100%',
              marginTop: isMobile ? '14px' : '16px',
              padding: isMobile ? '12px' : '12px 24px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {t('enable2FA')}
          </button>
        )}
      </div>

      {/* Security Items */}
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: isMobile ? '12px' : '16px',
        overflow: 'hidden',
      }}>
        {securityItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={index}
              onClick={item.action}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: isMobile ? '14px 16px' : '20px 24px',
                borderBottom: index < securityItems.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                gap: isMobile ? '12px' : '16px',
              }}
            >
              <div style={{
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                minWidth: isMobile ? '40px' : '48px',
                borderRadius: isMobile ? '10px' : '12px',
                backgroundColor: `${COLORS.primary}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Icon size={isMobile ? 20 : 24} color={COLORS.primary} />
              </div>
              
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  color: COLORS.text, 
                  fontSize: isMobile ? '14px' : '15px', 
                  fontWeight: '600',
                  marginBottom: '2px'
                }}>
                  {item.title}
                </div>
                <div style={{ 
                  color: COLORS.textSecondary, 
                  fontSize: isMobile ? '12px' : '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {item.description}
                </div>
              </div>

              {item.status && (
                <div style={{
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  borderRadius: '20px',
                  backgroundColor: item.status === 'enabled' ? '#0ecb8120' : '#f6465d20',
                  color: item.status === 'enabled' ? '#0ecb81' : '#f6465d',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}>
                  {item.status === 'enabled' ? t('on') : t('off')}
                </div>
              )}

              <ChevronRight size={isMobile ? 18 : 20} color={COLORS.textSecondary} style={{ flexShrink: 0 }} />
            </div>
          );
        })}
      </div>

      {/* Security Tips - Hidden on mobile for cleaner look */}
      {!isMobile && (
        <div style={{
          marginTop: '32px',
          padding: '20px 24px',
          backgroundColor: '#f0b90b15',
          border: '1px solid #f0b90b40',
          borderRadius: '12px',
        }}>
          <div style={{ 
            color: COLORS.primary, 
            fontSize: '14px', 
            fontWeight: '600',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={18} />
            {t('securityTips')}
          </div>
          <ul style={{ 
            color: COLORS.textSecondary, 
            fontSize: '13px',
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.8'
          }}>
            <li>{t('securityTip1')}</li>
            <li>{t('securityTip2')}</li>
            <li>{t('securityTip3')}</li>
            <li>{t('securityTip4')}</li>
          </ul>
        </div>
      )}

      {/* Modals */}
      <ChangePasswordModal 
        isOpen={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)} 
      />
      <TwoFactorModal 
        isOpen={show2FAModal} 
        onClose={() => {
          setShow2FAModal(false);
          loadSecurityStatus();
        }} 
      />
      <ActiveSessionsModal 
        isOpen={showSessionsModal} 
        onClose={() => setShowSessionsModal(false)} 
      />
      <IPWhitelistModal 
        isOpen={showIPModal} 
        onClose={() => setShowIPModal(false)} 
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SecurityPage;
